import type { FC } from 'react';
import { Draggable } from 'react-beautiful-dnd';

import { DragDropContainer } from 'components/drap-drop/DrapDropContainer';
import { SubTitle } from 'components/text/SubTitle';
import { Thumbnail } from 'components/thumbnail';
import { Typography } from 'enable-ui';

import { Body, CountInput, TableHeader } from '../styles/dashboard';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as DrapDropIcon } from 'assets/icons/drap-drop.svg';
import { ReactComponent as DuplicateIcon } from 'assets/icons/duplicate.svg';

import type { FunctionalTest } from '../core/type';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { FunctionalTestMethod } from '../core/enum';
import { CustomHeader } from './CustomHeader';

const FunctionalTestTableHeader = () => {
  return (
    <TableHeader style={{ minHeight: 48, padding: '8px 0' }}>
      <div style={{ minWidth: '24px' }}></div>
      <div style={{ minWidth: '42px' }}>
        <Typography
          style={{ marginRight: 16, textAlign: 'center', color: COLOR_CONFIG['neutral-text'] }}
          weight={600}
        >
          #
        </Typography>
      </div>
      <CustomHeader
        title="Functional test"
        styleHeader={{ width: '100%', display: 'flex', flexGrow: 1, padding: '0 8px' }}
        id="name"
      />
      <CustomHeader title="" styleHeader={{ minWidth: '32px', whiteSpace: 'normal' }} id="id" />

      <div style={{ minWidth: '44px' }}></div>
    </TableHeader>
  );
};

export const FunctionalTestTable: FC<{
  functionalTests: FunctionalTest[];
  onDelete: (index: number) => void;
  onDragEnd: (result: any) => void;
}> = ({ functionalTests, onDelete, onDragEnd }) => {
  const renderBodyTable = (item: FunctionalTest, index: number) => {
    return (
      <Body
        $color={(index + 1) % 2 === 0 ? COLOR_CONFIG.doctor : COLOR_CONFIG.white}
        style={{
          // borderBottom: `1px solid ${neutral['neutral-3']}`,
          padding: '12px 0',
        }}
        key={index}
      >
        <div style={{ width: '24px', padding: '6px 0 0 0 ' }}>
          <DrapDropIcon />
        </div>
        <div style={{ width: '42px', padding: '0 8px' }}>
          <Typography
            style={{
              textAlign: 'center',
              fontSize: 18,
              lineHeight: '26px',
              color: COLOR_CONFIG['neutral-text'],
            }}
          >
            {index + 1}.
          </Typography>
        </div>
        <div
          style={{
            paddingLeft: 16,
            width: 'calc(100% - 98px)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div>
            <Thumbnail thumbnail={item.thumbnail} />
          </div>
          <article>
            <Typography
              size={16}
              height={22}
              weight={600}
              color={COLOR.surfaceNeutralBold['neutral-11']}
              variant="h5"
            >
              {item.name}
            </Typography>
            {item.type ? (
              <SubTitle leftTitle={'Score entry'} rightTitle={item.type} />
            ) : (
              <Typography size={14} height={20} color={COLOR_CONFIG['neutral-text']}>
                {item.method?.toLowerCase() === FunctionalTestMethod.Kg
                  ? 'Weight entry'
                  : item.method?.toLowerCase() === FunctionalTestMethod.Time
                    ? 'Time entry'
                    : 'Rep count entry'}
              </Typography>
            )}

            {item.isSO2AndHR && (
              <Typography
                style={{
                  fontSize: '16px',
                  lineHeight: '22px',
                  fontWeight: 400,
                  color: '#808080',
                }}
              >
                SpO2 and heart rate
              </Typography>
            )}
          </article>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '6px 0 0 0 ' }}>
          {/* <DuplicateIcon style={{ cursor: 'pointer', width: 32 }} /> */}
          <CloseIcon style={{ cursor: 'pointer', width: 32 }} onClick={() => onDelete(index)} />
        </div>
      </Body>
    );
  };

  return (
    <div style={{ overflow: 'auto' }}>
      <div style={{ minWidth: 'max-content' }}>
        <FunctionalTestTableHeader />
        {functionalTests.length ? (
          <div style={{ paddingBottom: 46, minWidth: '100%' }}>
            <DragDropContainer onDragEnd={onDragEnd}>
              {functionalTests.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(providedItem) => (
                    <div
                      ref={providedItem.innerRef}
                      {...providedItem.draggableProps}
                      {...providedItem.dragHandleProps}
                    >
                      {renderBodyTable(item, index)}
                    </div>
                  )}
                </Draggable>
              ))}
            </DragDropContainer>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};
